import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";
import ImageUpload from "@/components/admin/ImageUpload";
import { useSettingsEditor, SaveBar } from "@/components/admin/useSettingsEditor";

const ROLE_ICONS = [
  "Stethoscope", "HeartPulse", "Syringe", "Users", "Brain", "Baby",
  "GraduationCap", "Briefcase", "FlaskConical", "ShieldCheck", "Home", "Award",
];

function StringListEditor({ label, items, onChange, placeholder }) {
  const set = (i, v) => { const next = [...items]; next[i] = v; onChange(next); };
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</p>
        <Button variant="outline" size="sm" onClick={() => onChange([...items, ""])} className="rounded-full h-9"><Plus className="w-4 h-4 mr-1.5" />Add</Button>
      </div>
      <div className="space-y-2">
        {items.map((v, i) => (
          <div key={i} className="flex gap-2">
            <Input value={v} onChange={(e) => set(i, e.target.value)} placeholder={placeholder} className="h-10 rounded-lg border-slate-200" />
            <button className="text-rose-500 p-2" onClick={() => onChange(items.filter((_, idx) => idx !== i))}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminCareers() {
  const { draft, patchPath, save, reset, saving } = useSettingsEditor();
  if (!draft) return null;
  const c = draft.careers || {};
  const roles = c.roles || [];
  const gov = c.governmentTags || [];
  const intl = c.internationalCountries || [];

  const setRole = (i, k, v) => {
    const next = [...roles]; next[i] = { ...next[i], [k]: v };
    patchPath("careers.roles", next);
  };
  const setIntl = (i, k, v) => {
    const next = [...intl]; next[i] = { ...next[i], [k]: v };
    patchPath("careers.internationalCountries", next);
  };

  return (
    <div data-testid="cms-careers-page">
      <div className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] brand-secondary-text mb-2">Careers</p>
        <h1 className="font-heading text-3xl sm:text-4xl text-slate-900">Careers Page</h1>
        <p className="text-slate-500 mt-2">Manage the Careers page content — roles, government opportunities and international destinations.</p>
      </div>

      <div className="space-y-6">
        {/* Header copy */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 sm:p-8 space-y-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Page Header</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div><Label className="text-slate-700 text-sm">Eyebrow</Label>
              <Input value={c.eyebrow || ""} onChange={(e) => patchPath("careers.eyebrow", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
            </div>
            <div><Label className="text-slate-700 text-sm">Heading</Label>
              <Input value={c.heading || ""} onChange={(e) => patchPath("careers.heading", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
            </div>
            <div className="sm:col-span-2"><Label className="text-slate-700 text-sm">Intro paragraph</Label>
              <Textarea value={c.intro || ""} onChange={(e) => patchPath("careers.intro", e.target.value)} className="mt-1.5 rounded-xl border-slate-200 min-h-[100px]" />
            </div>
            <div className="sm:col-span-2">
              <ImageUpload label="Header Image" value={c.image} onChange={(url) => patchPath("careers.image", url)} folder="images" testid="careers-image" />
            </div>
          </div>
        </div>

        {/* Career Paths */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 sm:p-8 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div><Label className="text-slate-700 text-sm">Roles Section Heading</Label>
              <Input value={c.rolesHeading || ""} onChange={(e) => patchPath("careers.rolesHeading", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
            </div>
          </div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Career Roles</p>
            <Button variant="outline" size="sm" onClick={() => patchPath("careers.roles", [...roles, { title: "New Role", icon: "Stethoscope" }])} className="rounded-full h-9">
              <Plus className="w-4 h-4 mr-1.5" /> Add Role
            </Button>
          </div>
          <div className="space-y-2">
            {roles.map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center rounded-lg border border-slate-200 p-2">
                <select value={r.icon || "Stethoscope"} onChange={(e) => setRole(i, "icon", e.target.value)}
                  className="col-span-4 h-10 rounded-lg border border-slate-200 text-sm px-2 bg-white">
                  {ROLE_ICONS.map((op) => (<option key={op} value={op}>{op}</option>))}
                </select>
                <Input value={r.title || ""} onChange={(e) => setRole(i, "title", e.target.value)} className="col-span-7 h-10 rounded-lg border-slate-200" />
                <button className="text-rose-500 p-2" onClick={() => patchPath("careers.roles", roles.filter((_, idx) => idx !== i))}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>

        {/* Government */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 sm:p-8 space-y-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Government Sector</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div><Label className="text-slate-700 text-sm">Heading</Label>
              <Input value={c.governmentHeading || ""} onChange={(e) => patchPath("careers.governmentHeading", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
            </div>
            <div className="sm:col-span-2"><Label className="text-slate-700 text-sm">Intro</Label>
              <Textarea value={c.governmentIntro || ""} onChange={(e) => patchPath("careers.governmentIntro", e.target.value)} className="mt-1.5 rounded-xl border-slate-200 min-h-[80px]" />
            </div>
          </div>
          <StringListEditor label="Government Tags" items={gov} onChange={(v) => patchPath("careers.governmentTags", v)} placeholder="e.g. AIIMS" />
        </div>

        {/* International */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 sm:p-8 space-y-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">International</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div><Label className="text-slate-700 text-sm">Heading</Label>
              <Input value={c.internationalHeading || ""} onChange={(e) => patchPath("careers.internationalHeading", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
            </div>
            <div className="sm:col-span-2"><Label className="text-slate-700 text-sm">Intro</Label>
              <Textarea value={c.internationalIntro || ""} onChange={(e) => patchPath("careers.internationalIntro", e.target.value)} className="mt-1.5 rounded-xl border-slate-200 min-h-[80px]" />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Countries</p>
              <Button variant="outline" size="sm" onClick={() => patchPath("careers.internationalCountries", [...intl, { name: "New Country", flag: "🌍" }])} className="rounded-full h-9">
                <Plus className="w-4 h-4 mr-1.5" /> Add Country
              </Button>
            </div>
            <div className="space-y-2">
              {intl.map((cn, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center rounded-lg border border-slate-200 p-2">
                  <Input value={cn.flag || ""} onChange={(e) => setIntl(i, "flag", e.target.value)} placeholder="🇮🇳" className="col-span-2 h-10 rounded-lg border-slate-200 text-center" />
                  <Input value={cn.name || ""} onChange={(e) => setIntl(i, "name", e.target.value)} placeholder="Country name" className="col-span-9 h-10 rounded-lg border-slate-200" />
                  <button className="text-rose-500 p-2" onClick={() => patchPath("careers.internationalCountries", intl.filter((_, idx) => idx !== i))}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Closing */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 sm:p-8 space-y-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Closing CTA</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div><Label className="text-slate-700 text-sm">Heading</Label>
              <Input value={c.closingHeading || ""} onChange={(e) => patchPath("careers.closingHeading", e.target.value)} className="mt-1.5 h-11 rounded-xl border-slate-200" />
            </div>
            <div className="sm:col-span-2"><Label className="text-slate-700 text-sm">Text</Label>
              <Textarea value={c.closingText || ""} onChange={(e) => patchPath("careers.closingText", e.target.value)} className="mt-1.5 rounded-xl border-slate-200 min-h-[70px]" />
            </div>
          </div>
        </div>
      </div>

      <SaveBar onSave={save} onReset={reset} saving={saving} />
    </div>
  );
}
